// src/admin/components/POReturnDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { PurchaseOrder, ReturnItem, ReturnReason, RETURN_REASON_LABELS } from './PurchaseOrder';
import { PurchaseOrderReturnService } from './purchaseOrderReturnService';
import { useAuth } from '../../../contexts/AuthContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import { useCompany } from '../../../contexts/CompanyContext';
import { formatCurrency } from '../../../util/Formatcurrency';

interface POReturnDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  purchaseOrder: PurchaseOrder;
}

const POReturnDialog: React.FC<POReturnDialogProps> = ({
  open, onClose, onSuccess, purchaseOrder: po,
}) => {
  const { currentUser } = useAuth();
  const { companyProfile } = useCompany();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isDark = theme.palette.mode === 'dark';

  const companyCurrency = companyProfile?.currency || 'LKR';
  const currencySymbol = getCurrencySymbol(companyCurrency);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [returnItems, setReturnItems] = useState<ReturnItem[]>([]);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [images, setImages] = useState<File[]>([]);

  useEffect(() => {
    if (open) {
      const items: ReturnItem[] = po.items.map(item => ({
        id: `return-${item.id || Date.now()}`,
        productId: item.productId,
        productName: item.productName,
        variantId: item.variantId,
        variantName: item.variantName,
        sku: item.sku,
        quantity: 0,
        unitPrice: item.unitPrice,
        totalPrice: 0,
        reason: ReturnReason.DAMAGED,
        notes: '',
        warehouseId: item.warehouseId,
        warehouseName: item.warehouseName,
        images: [],
      }));
      setReturnItems(items);
      setReason('');
      setNotes('');
      setInternalNotes('');
      setImages([]);
      setError('');
    }
  }, [open, po]);

  const handleItemChange = (index: number, field: keyof ReturnItem, value: any) => {
    const newItems = [...returnItems];
    newItems[index] = { ...newItems[index], [field]: value };
    if (field === 'quantity') newItems[index].totalPrice = value * newItems[index].unitPrice;
    setReturnItems(newItems);
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) setImages([...images, ...Array.from(event.target.files)]);
  };

  const handleRemoveImage = (index: number) => setImages(images.filter((_, i) => i !== index));

  const totalAmount = returnItems.reduce((sum, item) => sum + item.totalPrice, 0);

  const handleSubmit = async () => {
    if (!currentUser) return;
    const itemsToReturn = returnItems.filter(item => item.quantity > 0);
    if (itemsToReturn.length === 0) { setError('Please select at least one item to return with quantity > 0'); return; }

    for (const returnItem of itemsToReturn) {
      const poItem = po.items.find(item => item.productId === returnItem.productId && (item.variantId || '') === (returnItem.variantId || ''));
      if (!poItem) { setError(`Product ${returnItem.productName} not found in purchase order`); return; }
      if (returnItem.quantity > poItem.receivedQuantity) {
        setError(`Return quantity for ${returnItem.productName} exceeds received quantity (${poItem.receivedQuantity})`);
        return;
      }
      if (!returnItem.warehouseId) { setError(`No warehouse assigned for ${returnItem.productName}`); return; }
    }

    if (!reason.trim()) { setError('Please provide a reason for the return'); return; }

    try {
      setLoading(true);
      setError('');
      await PurchaseOrderReturnService.createReturn(
        { purchaseOrderId: po.id!, items: itemsToReturn, reason, notes, internalNotes, images },
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'Unknown'
      );
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create return');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => { setError(''); onClose(); };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 680, md: 800 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

          {/* Header */}
          <Box sx={{
            px: 3, py: 2,
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <Box>
              <Typography variant="h6">Create Return</Typography>
              <Typography variant="body2" color="text.secondary">{po.poNumber}</Typography>
            </Box>
            <IconButton onClick={handleClose} edge="end" disabled={loading}><CloseIcon /></IconButton>
          </Box>

          {/* Scrollable Content */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Grid container spacing={3}>
              {/* Supplier Info */}
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    backgroundImage: 'none',
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Supplier Information</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{po.supplierName}</Typography>
                  <Typography variant="body2" color="text.secondary">{po.supplierEmail} | {po.supplierPhone}</Typography>
                </Paper>
              </Grid>

              {/* Return Items */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Select Items to Return</Typography>
                <Paper variant="outlined" sx={{ overflow: 'auto', bgcolor: 'background.paper', backgroundImage: 'none' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Warehouse</TableCell>
                        <TableCell align="right">Received</TableCell>
                        <TableCell align="right">Return Qty</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Reason</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {returnItems.map((item, index) => {
                        const poItem = po.items.find(
                          pi => pi.productId === item.productId && (pi.variantId || '') === (item.variantId || '')
                        );
                        const maxQuantity = poItem?.receivedQuantity || 0;

                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.productName}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.variantName ? `${item.variantName} · ` : ''}{item.sku}
                              </Typography>
                              {/* Warehouse on mobile */}
                              {item.warehouseName && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                                  {item.warehouseName}
                                </Typography>
                              )}
                              {/* Reason on mobile */}
                              <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 0.5 }}>
                                <FormControl size="small" sx={{ minWidth: 110 }}>
                                  <Select value={item.reason} onChange={(e) => handleItemChange(index, 'reason', e.target.value)} disabled={loading}>
                                    {Object.entries(RETURN_REASON_LABELS).map(([key, label]) => (
                                      <MenuItem key={key} value={key}>{label}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                              {item.warehouseName
                                ? <Chip label={item.warehouseName} size="small" variant="outlined" />
                                : <Typography variant="caption" color="error">No warehouse</Typography>}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">{maxQuantity}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number" size="small"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', Math.min(parseInt(e.target.value) || 0, maxQuantity))}
                                inputProps={{ min: 0, max: maxQuantity }}
                                sx={{ width: 70 }}
                                disabled={loading}
                              />
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <Select value={item.reason} onChange={(e) => handleItemChange(index, 'reason', e.target.value)} disabled={loading}>
                                  {Object.entries(RETURN_REASON_LABELS).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>{label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: item.quantity > 0 ? 600 : 400, color: item.quantity > 0 ? 'error.main' : 'text.secondary' }}
                              >
                                {formatCurrency(item.totalPrice, currencySymbol)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>

              {/* Total */}
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    backgroundImage: 'none',
                    bgcolor: isDark ? 'rgba(239,83,80,0.10)' : 'rgba(239,83,80,0.06)',
                    borderColor: isDark ? 'rgba(239,83,80,0.40)' : 'rgba(239,83,80,0.30)',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Total Return Amount:</Typography>
                    <Typography variant="h5" color="error.main" sx={{ fontWeight: 700 }}>
                      {formatCurrency(totalAmount, currencySymbol)}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>

              {/* Reason */}
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Return Reason *" multiline rows={2} value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Describe the reason for this return..."
                  disabled={loading}
                />
              </Grid>

              {/* Notes */}
              {/* <Grid item xs={12}>
                <TextField
                  fullWidth label="Notes (Visible to Supplier)" multiline rows={2} value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes for the supplier..."
                  disabled={loading}
                />
              </Grid> */}

              {/* Internal Notes */}
              {/* <Grid item xs={12}>
                <TextField
                  fullWidth label="Internal Notes" multiline rows={2} value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Internal notes (not visible to supplier)..."
                  disabled={loading}
                />
              </Grid> */}

              {/* Image Upload */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Supporting Images (Optional)</Typography>
                <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} sx={{ mb: 2 }} disabled={loading}>
                  Upload Images
                  <input type="file" hidden multiple accept="image/*" onChange={handleImageSelect} />
                </Button>
                {images.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {images.map((image, index) => (
                      <Chip key={index} label={image.name} onDelete={() => handleRemoveImage(index)} deleteIcon={<DeleteIcon />} />
                    ))}
                  </Stack>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* Footer */}
          <Divider />
          <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', flexShrink: 0 }}>
            <Button onClick={handleClose} disabled={loading} startIcon={<CancelIcon />}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="error"
              disabled={loading || totalAmount === 0}
              startIcon={<SaveIcon />}
            >
              Create Return
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* ── Full-screen saving overlay ─────────────────────────────────────── */}
      <Backdrop
        open={loading}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              Creating Return…
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>
      {/* ────────────────────────────────────────────────────────────────────── */}
    </>
  );
};

export default POReturnDialog;